import { prepareRequestUrl } from './utils'

export interface IHttpClientRequestOptions {
  baseUrl?: string
  withCredentials?: boolean
  headers?: Record<string, string>
  signal?: AbortSignal
}

export default class HttpClient {
  baseUrl: string
  headers: Record<string, string>
  withCredentials: boolean
  constructor(options: {
    baseUrl: string
    headers?: Record<string, string>
    withCredentials?: boolean
  }) {
    this.baseUrl = options.baseUrl
    this.headers = options.headers || { 'Content-Type': 'application/json' }
    this.withCredentials = options.withCredentials || false

    this._makeRequest = this._makeRequest.bind(this)
  }

  private async _makeRequest<T, U = null>(
    url: string,
    method: string,
    body: U,
    options: IHttpClientRequestOptions = {},
  ) {
    let data: T = null
    const _withCredentials =
      options.withCredentials !== undefined
        ? options.withCredentials
        : this.withCredentials
    const combinedHeaders = { ...this.headers, ...options.headers }

    const res = await fetch(prepareRequestUrl(url, this.baseUrl), {
      method,
      headers: combinedHeaders,
      body: body ? JSON.stringify(body) : null,
      credentials: _withCredentials ? 'include' : 'same-origin',
      signal: options.signal,
    })

    try {
      const contentType = res.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        data = (await res.json()) as T
      } else if (contentType && contentType.includes('text/')) {
        data = (await res.text()) as T
      } else {
        data = (await res.blob()) as T
      }
    } catch (error) {
      // console.error(error);
    }

    return {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
      url: res.url,
      redirected: res.redirected,
      ok: res.ok,
      data,
    }
  }

  public async get<T>(url: string, options: IHttpClientRequestOptions = {}) {
    return this._makeRequest<T>(url, 'GET', null, options)
  }

  public async post<T, U>(
    url: string,
    body: U = null,
    options: IHttpClientRequestOptions = {},
  ) {
    return this._makeRequest<T, U>(url, 'POST', body, options)
  }

  public async put<T, U = null>(
    url: string,
    body: U = null,
    options: IHttpClientRequestOptions = {},
  ) {
    return this._makeRequest<T, U>(url, 'PUT', body, options)
  }

  public async delete<T, U = null>(
    url: string,
    body: U = null,
    options: IHttpClientRequestOptions = {},
  ) {
    return this._makeRequest<T, U>(url, 'DELETE', body, options)
  }

  public async patch<T, U = null>(
    url: string,
    body: U = null,
    options: IHttpClientRequestOptions = {},
  ) {
    return this._makeRequest<T, U>(url, 'PATCH', body, options)
  }

  public async head<T, U = null>(
    url: string,
    body: U = null,
    options: IHttpClientRequestOptions = {},
  ) {
    return this._makeRequest<T, U>(url, 'HEAD', body, options)
  }

  public async options<T, U = null>(
    url: string,
    body: U = null,
    options: IHttpClientRequestOptions = {},
  ) {
    return this._makeRequest<T, U>(url, 'OPTIONS', body, options)
  }

  public addHeaders(headers: Record<string, string>) {
    this.headers = { ...this.headers, ...headers }
  }

  public removeHeader(key: string) {
    delete this.headers[key]
  }

  public clearHeaders() {
    this.headers = {}
  }

  public setWithCredentials(value: boolean) {
    this.withCredentials = value
  }
}
